/*
 * decaffeinate suggestions:
 * DS101: Remove unnecessary use of Array.from
 * DS102: Remove unnecessary code created because of implicit returns
 * DS205: Consider reworking code to avoid use of IIFEs
 * DS207: Consider shorter variations of null checks
 * Full docs: https://github.com/decaffeinate/decaffeinate/blob/main/docs/suggestions.md
 */
import _ from 'underscore';
import objectPath from 'object-path-immutable';
import moment from 'moment';
import { NON_TAXABLE_PRODUCTS } from '../components/constants/tax_products';

import * as Actions from './actions';

//# Registration editor Reducer
//
// The state for the registration editor consists of:
//   attendee_questions: List of attendee questions for the event
//   payout_accounts: Available payout accounts for the user to choose from
//   promo_codes: List of promo codes for the event
//   ticket_groups: List of ticket groups for the event
//   ticket_classes: List of ticket classes for the event
//   dragging: Various state for drag-drop operations in progress
//     ticket_classes: State for tracking in-progress drag of ticket classes
//       dragging_id: ID of ticket class currently being dragged
//       previous: Position and ticket group of ticket class before dragging
//     ticket_groups: State for tracking in-progress drag of ticket groups
//       (see ticket_classes)
//
// The lists are flat arrays, not keyed by IDs, and order isn't really important.
// Therefore, lots of the reducer actions will find the index by ID.

// Return a blank ticket class array
const newTicketClass = function(attributes) {
  if (attributes == null) { attributes = {}; }
  const new_id = attributes.id || Actions.getId('ticket');

  const nextAttributes = _.extend({
    id: new_id,
    name: "",
    price: 0,
    customer_pays_fees: true,
    hidden: false,
    active: true,
    create: true,
    delete: false,
    ticket_type: 'in_person',
    virtual_event_message: "",
    virtual_event_url: "",
    taxable: false,
    tax_product: ''
  },
   attributes);

  // Enable the taxable flag by default, if it's not set to a non-taxable product
  if (!NON_TAXABLE_PRODUCTS.includes(nextAttributes.tax_product)) { nextAttributes.taxable = true; }

  return nextAttributes;
};

// Return a blank ticket group hash
const newTicketGroup = function(attributes) {
  if (attributes == null) { attributes = {}; }
  const new_id = attributes.id || Actions.getId('ticket_group');

  return _.extend({
    id: new_id,
    name: "",
    create: true,
    delete: false,
    ticket_class_ids: []
  },
   attributes);
};

// Return a blank promo code hash
const newPromoCode = function(attributes) {
  if (attributes == null) { attributes = {}; }
  const new_id = attributes.id || Actions.getId('promo_code');

  return _.extend({
    id: new_id,
    code: '',
    discount_type: 'percent',
    active: true,
    create: true,
    delete: false,
    ticket_class_ids: []
  },
   attributes);
};

// Return a blank attendee question hash
const newAttendeeQuestion = function(attributes) {
  if (attributes == null) { attributes = {}; }
  const new_id = attributes.id || Actions.getId('question');

  return _.extend({
    id: new_id,
    question: '',
    required: false,
    response_type: 'string',
    active: true,
    create: true,
    delete: false,
    choices: ['', '']
  },
   attributes);
};

// Helper to parse a string to a Date object, or return the value unchanged
const parseDate = function(date) {
  const parsed = moment(date);
  if (parsed.isValid()) {
    return parsed.toDate();
  }
  return date;
};

// For all ticket classes, make sure the position value is contiguous starting from 0
// within each ticket group
function renumberTicketClassPositions(state) {
  let newState = state;
  const classes = _.sortBy(state.ticket_classes, 'position');
  const positions = {'default': 0};

  for (const ticket_class of Array.from(classes)) {
    if (!positions[ticket_class.ticket_group_id]) { positions[ticket_class.ticket_group_id] = 0; }
    const index = _.findIndex(state.ticket_classes, {id: ticket_class.id});
    newState = objectPath.set(newState, ['ticket_classes', index, 'position'], positions[ticket_class.ticket_group_id]);
    positions[ticket_class.ticket_group_id] += 1;
  }

  return newState;
}

// Move the ticket class to the appropriate place, by adjusting position as necessary for all other classes
//
// This cheats a bit by keeping the position as if the tickets are in one large list, instead of sub-lists by ticket_group_id.
// The relation between values is the important part, not the absolute values.
const moveTicketClass = function(previousState, ticket_class_id, target_group_id, target_position) {
  if (target_position === 'end') { target_position = 99999; }
  if (target_position === 'top') { target_position = 0; }

  const ticket_index = _.findIndex(previousState.ticket_classes, {id: ticket_class_id});
  const current_position = previousState.ticket_classes[ticket_index].position;
  const current_group = previousState.ticket_classes[ticket_index].ticket_group_id;

  const current_group_index = _.findIndex(previousState.ticket_groups, {id: current_group});
  const target_group_index = _.findIndex(previousState.ticket_groups, {id: target_group_id});

  // If moving down, offset the target position so it feels more natural when dragging down
  if (target_position > current_position) { target_position += 1; }

  let newState = previousState;

  // If switching between groups, update the list of ticket_class_ids on each
  //
  // The ticket_group_id and position on the ticket class is set below
  if (current_group_index !== target_group_index) {
    // Remove from current group
    if (current_group_index > -1) {
      const class_ids = newState.ticket_groups[current_group_index].ticket_class_ids;
      newState = objectPath.set(newState, ['ticket_groups', current_group_index, 'ticket_class_ids'], _.reject(class_ids, i => i === ticket_class_id));
    }

    // Add to new group
    if (target_group_index > -1) {
      newState = objectPath.push(newState, ['ticket_groups', target_group_index, 'ticket_class_ids'], ticket_class_id);
    }
  }

  newState = objectPath(newState)
    // Move lower items to make room for the current one
    .update(['ticket_classes'], ticket_classes => Array.from(ticket_classes).map((ticket_class) =>
    (ticket_class.position >= target_position ?
      objectPath.set(ticket_class, 'position', ticket_class.position + 1)
    :
      ticket_class))).set(['ticket_classes', ticket_index, 'position'], target_position)
    .set(['ticket_classes', ticket_index, 'ticket_group_id'], target_group_id)
    .value();

  // Adjust positions to be contiguous
  return renumberTicketClassPositions(newState);
};

// For all ticket groups, make sure the position value is contiguous starting from 0
function renumberTicketGroupPositions(state) {
  let newState = state;
  const groups = _.sortBy(state.ticket_groups, 'position');
  let position = 0;

  for (const ticket_group of Array.from(groups)) {
    const index = _.findIndex(state.ticket_groups, {id: ticket_group.id});
    newState = objectPath.set(newState, ['ticket_groups', index, 'position'], position);
    position += 1;
  }

  return newState;
}

// Move the ticket group to the appropriate place, by adjusting position as necessary for all other classes
const moveTicketGroup = function(previousState, ticket_group_id, target_position) {
  if (target_position === 'end') { target_position = 99999; }
  if (target_position === 'top') { target_position = 0; }

  const ticket_group_index = _.findIndex(previousState.ticket_groups, {id: ticket_group_id});
  const current_position = previousState.ticket_groups[ticket_group_index].position;

  // If moving down, offset the target position so it feels more natural when dragging down
  if (target_position > current_position) { target_position += 1; }

  const newState = objectPath(previousState)
    // Move lower items to make room for the current one
    .update(['ticket_groups'], ticket_groups => Array.from(ticket_groups).map((ticket_group) =>
    (ticket_group.position >= target_position ?
      objectPath.set(ticket_group, 'position', ticket_group.position + 1)
    :
      ticket_group))).set(['ticket_groups', ticket_group_index, 'position'], target_position)
    .value();

  // Adjust positions to be contiguous
  return renumberTicketGroupPositions(newState);
};

// For all attendee questions, make sure the position value is contiguous starting from 0
function renumberAttendeeQuestionPositions(state) {
  let newState = state;
  const attendee_questions = _.sortBy(state.attendee_questions, 'position');
  let position = 0;

  for (const attendee_question of Array.from(attendee_questions)) {
    const index = _.findIndex(state.attendee_questions, {id: attendee_question.id});
    newState = objectPath.set(newState, ['attendee_questions', index, 'position'], position);
    position += 1;
  }

  return newState;
}

// Move the attendee question to the appropriate place, by adjusting position as necessary for all other questions
const moveAttendeeQuestion = function(previousState, attendee_question_id, target_position) {
  if (target_position === 'end') { target_position = 99999; }
  if (target_position === 'top') { target_position = 0; }

  const attendee_question_index = _.findIndex(previousState.attendee_questions, {id: attendee_question_id});
  const current_position = previousState.attendee_questions[attendee_question_index].position;

  // If moving down, offset the target position so it feels more natural when dragging down
  if (target_position > current_position) { target_position += 1; }

  const newState = objectPath(previousState)
    // Move lower items to make room for the current one
    .update(['attendee_questions'], attendee_questions => Array.from(attendee_questions).map((attendee_question) =>
    (attendee_question.position >= target_position ?
      objectPath.set(attendee_question, 'position', attendee_question.position + 1)
    :
      attendee_question))).set(['attendee_questions', attendee_question_index, 'position'], target_position)
    .value();

  // Adjust positions to be contiguous
  return renumberAttendeeQuestionPositions(newState);
};

// Reorder the choices array for question_id to place item at target_index
const moveAttendeeQuestionChoice = function(state, question_id, item, target_index) {
  const attendee_question_index = _.findIndex(state.attendee_questions, {id: question_id});
  const attendee_question = state.attendee_questions[attendee_question_index];

  // Find the previous location of the item
  const previous_index = _.indexOf(attendee_question.choices, item);

  if (previous_index === target_index) { return state; }

  if (target_index === 'end') { target_index = attendee_question.choices.length; }

  // Copy choices
  const next_choices = attendee_question.choices.slice();
  // And move the item to the new location at target_index
  if (previous_index !== -1) { next_choices.splice(previous_index, 1); }
  next_choices.splice(target_index, 0, item);

  return objectPath.set(state, ['attendee_questions', attendee_question_index, 'choices'], next_choices);
};

// Update the payout-account related settings under `settings` based on the selected payout account
const updatePayoutAccountSettings = function(state) {
  // Get currently payout account selection and details from the state
  const {
    payout_account_id
  } = state.event;

  // Find matching payout account, by comparing as string IDs
  const payout_account = _.find(state.payout_accounts, payout_account => String(payout_account.id) === String(payout_account_id));
  if (payout_account) {
    // Update the settings state with the selected payout account's fee structure and support flags
    const new_fees = {
      payment: {
        base: payout_account.provider_fee_base,
        percent: payout_account.provider_fee_percent
      },
      localist: {
        base: payout_account.localist_fee_base,
        percent: payout_account.localist_fee_percent
      },
      localist_item: {
        base: payout_account.localist_item_fee_base,
        percent: payout_account.localist_item_fee_percent
      }
    };

    return objectPath(state)
      .set(['settings', 'has_payout_account'], !!payout_account_id)
      .set(['settings', 'customer_pays_fees_supported'], payout_account['supports_customer_pays_fees?'])
      .set(['settings', 'payout_account_supports_tax'], payout_account['supports_tax?'])
      .set(['settings', 'fees'], new_fees)
      .value();

  }
  // No payout account was found
  return objectPath(state)
    .set(['settings', 'has_payout_account'], false)
    .value();
};


export default function reducer(previousState, action) {
  let new_value;
  switch (action.type) {
    case Actions.SET_TICKET_CLASSES_GROUPS_ATTENDEE_QUESTIONS_PROMOS:
      return objectPath(previousState)
        .set(['ticket_classes'], action.payload.ticket_classes)
        .set(['ticket_groups'], action.payload.ticket_groups)
        .set(['attendee_questions'], action.payload.attendee_questions)
        .set(['promo_codes'], action.payload.promo_codes)
        .value();

    case Actions.EVENT_EXPERIENCE_CHANGE:
      return objectPath(previousState)
        .set(['event', 'experience'], action.payload.experience)
        .value();

    case Actions.EVENT_STREAM_URL_CHANGE:
      return objectPath(previousState)
        .set(['event', 'stream_url'], action.payload.stream_url)
        .value();

    case Actions.EVENT_HAS_ADDRESS_CHANGE:
      return objectPath(previousState)
        .set(['settings', 'event_has_address'], action.payload.has_address)
        .value();

    case Actions.EVENT_PAYOUT_ACCOUNT_ID_CHANGE:
      var nextState = objectPath(previousState)
        .set(['event', 'payout_account_id'], action.payload.payout_account_id)
        .value();

      return updatePayoutAccountSettings(nextState);

    case Actions.TICKET_CLASS_CHANGE:
      var update_index = _.findIndex(previousState.ticket_classes, {id: action.payload.id});

      switch (action.payload.name) {
        case 'price': case 'min_price': case 'max_price':
          // Price inputs are cents
          new_value = Number(action.payload.value);
          break;
        default:
          new_value = action.payload.value;
      }

      return objectPath.set(previousState, ['ticket_classes', update_index, action.payload.name], new_value);

    case Actions.TICKET_CLASS_ADD:
      var event_id = previousState.eventId;
      var {
        stream_url
      } = previousState.event;
      // Make the new ticket virtual only if the event's experience is virtual
      //
      // Note there is also a difference in the value for in-person: "in_person" here vs "inperson" on the experience
      var experience = previousState.event.experience === 'virtual' ?
                     'virtual'
                   :
                     'in_person';

      // Append the new ticket class
      var new_position = previousState.ticket_classes.length;
      var new_ticket_class = newTicketClass({
        position: new_position,
        event_id,
        id: action.payload.id,
        ticket_type: experience,
        virtual_event_url: stream_url,
        tax_product: previousState.settings.default_tax_product
      });
      var next_state = objectPath.push(previousState, 'ticket_classes', new_ticket_class);

      // and move it to the right spot
      return moveTicketClass(next_state, new_ticket_class.id, null, 'top');


    case Actions.TICKET_CLASS_REMOVE:
      update_index = _.findIndex(previousState.ticket_classes, {id: action.payload.id});

      return objectPath(previousState)
        .set(['ticket_classes', update_index, 'delete'], true)
        .set(['ticket_classes', update_index, 'create'], false)
        .value();

    case Actions.TICKET_CLASS_DRAG_START:
      // Stash the group/position for the ticket class and mark it as dragging
      var ticket_class = _.find(previousState.ticket_classes, {id: action.payload.id});

      return objectPath(previousState)
        .set(['dragging', 'ticket_classes', 'dragging_id'], ticket_class.id)
        .set(['dragging', 'ticket_classes', 'previous'], {
          ticket_group_id: ticket_class.ticket_group_id,
          position: ticket_class.position
        }).value();

    case Actions.TICKET_CLASS_DRAG_FINISH:
      // Reset dragging state
      return objectPath(previousState)
        .set(['dragging', 'ticket_classes', 'dragging_id'], null)
        .set(['dragging', 'ticket_classes', 'previous'], null)
      .value();

    case Actions.TICKET_CLASS_DRAG_CANCEL:
      // Revert state to old group/position
      var newState = moveTicketClass(previousState,
        action.payload.id,
        previousState.dragging.ticket_classes.previous.ticket_group_id,
        previousState.dragging.ticket_classes.previous.position);

      return objectPath(newState)
        .set(['dragging', 'ticket_classes', 'dragging_id'], null)
        .set(['dragging', 'ticket_classes', 'previous'], null)
      .value();

    case Actions.TICKET_CLASS_DRAG_UPDATE:
      return moveTicketClass(previousState,
        action.payload.item.id,
        action.payload.target_group_id,
        action.payload.target_position);


    case Actions.TICKET_GROUP_CHANGE:
      update_index = _.findIndex(previousState.ticket_groups, {id: action.payload.id});

      new_value = action.payload.value;
      return objectPath.set(previousState, ['ticket_groups', update_index, action.payload.name], new_value);

    case Actions.TICKET_GROUP_ADD:
      event_id = previousState.eventId;
      new_position = previousState.ticket_groups.length;
      var new_ticket_group = newTicketGroup({
        position: new_position,
        event_id,
        id: action.payload.id
      });

      next_state = objectPath.push(previousState, 'ticket_groups', new_ticket_group);

      // Move it to the right spot
      return moveTicketGroup(next_state, new_ticket_group.id, 'top');

    case Actions.TICKET_GROUP_REMOVE:
      update_index = _.findIndex(previousState.ticket_groups, {id: action.payload.id});

      next_state = objectPath(previousState)
        .set(['ticket_groups', update_index, 'delete'], true)
        .set(['ticket_groups', update_index, 'create'], false)
        .set(['ticket_groups', update_index, 'ticket_group_ids'], [])
        .value();

      // Move any ticket classes in the group to the end of the default group
      for (ticket_class of Array.from(next_state.ticket_classes)) {
        if (ticket_class.ticket_group_id === action.payload.id) {
          next_state = moveTicketClass(next_state, ticket_class.id, null, 'end');
        }
      }

      return next_state;

    case Actions.TICKET_GROUP_DRAG_START:
      // Stash the group/position for the ticket group and mark it as dragging
      var ticket_group = _.find(previousState.ticket_groups, {id: action.payload.id});

      return objectPath(previousState)
        .set(['dragging', 'ticket_groups', 'dragging_id'], ticket_group.id)
        .set(['dragging', 'ticket_groups', 'previous'],
          {position: ticket_group.position})
        .value();

    case Actions.TICKET_GROUP_DRAG_FINISH:
      // Reset dragging state
      return objectPath(previousState)
        .set(['dragging', 'ticket_groups', 'dragging_id'], null)
        .set(['dragging', 'ticket_groups', 'previous'], null)
      .value();

    case Actions.TICKET_GROUP_DRAG_CANCEL:
      // Revert state to old group/position
      newState = moveTicketGroup(previousState,
        action.payload.id,
        previousState.dragging.ticket_groups.previous.position);

      return objectPath(newState)
        .set(['dragging', 'ticket_groups', 'dragging_id'], null)
        .set(['dragging', 'ticket_groups', 'previous'], null)
      .value();

    case Actions.TICKET_GROUP_DRAG_UPDATE:
      return moveTicketGroup(previousState,
        action.payload.item.id,
        action.payload.target_position);


    case Actions.PROMO_CODE_CHANGE:
      update_index = _.findIndex(previousState.promo_codes, {id: action.payload.id});

      switch (action.payload.name) {
        case 'amount':
          // Price inputs are in cents
          new_value = Number(action.payload.value);
          break;
        default:
          new_value = action.payload.value;
      }

      return objectPath.set(previousState, ['promo_codes', update_index, action.payload.name], new_value);

    case Actions.PROMO_CODE_ADD:
      event_id = previousState.eventId;

      return objectPath.insert(previousState, 'promo_codes', newPromoCode({
        event_id,
        id: action.payload.id
      }), 0);

    case Actions.PROMO_CODE_REMOVE:
      update_index = _.findIndex(previousState.promo_codes, {id: action.payload.id});

      return objectPath(previousState)
        .set(['promo_codes', update_index, 'delete'], true)
        .set(['promo_codes', update_index, 'create'], false)
        .value();

    case Actions.PROMO_CODE_TICKET_CLASS_ADD:
      var promo_code_index = _.findIndex(previousState.promo_codes, {id: action.payload.id});

      if ((previousState.promo_codes[promo_code_index].ticket_class_ids != null ? previousState.promo_codes[promo_code_index].ticket_class_ids.indexOf(action.payload.ticket_class_id) : undefined) > -1) {
        // already added, so no change necessary
        return previousState;
      }
      // append value to the list
      return objectPath.push(previousState, ['promo_codes', promo_code_index, 'ticket_class_ids'], action.payload.ticket_class_id);

    case Actions.PROMO_CODE_TICKET_CLASS_REMOVE:
      promo_code_index = _.findIndex(previousState.promo_codes, {id: action.payload.id});
      var new_ticket_class_ids = _.reject(previousState.promo_codes[promo_code_index].ticket_class_ids, item => item === action.payload.ticket_class_id);
      return objectPath.set(previousState, ['promo_codes', promo_code_index, 'ticket_class_ids'], new_ticket_class_ids);


    case Actions.ATTENDEE_QUESTION_CHANGE:
      update_index = _.findIndex(previousState.attendee_questions, {id: action.payload.id});
      new_value = action.payload.value;
      return objectPath.set(previousState, ['attendee_questions', update_index, action.payload.name], new_value);

    case Actions.ATTENDEE_QUESTION_ADD:
      event_id = previousState.eventId;
      new_position = previousState.attendee_questions.length;

      next_state = objectPath.insert(previousState, 'attendee_questions', newAttendeeQuestion({
        position: new_position,
        event_id,
        id: action.payload.id
      }), 0);

      return moveAttendeeQuestion(next_state, action.payload.id, 'start');

    case Actions.ATTENDEE_QUESTION_REMOVE:
      update_index = _.findIndex(previousState.attendee_questions, {id: action.payload.id});

      return objectPath(previousState)
        .set(['attendee_questions', update_index, 'delete'], true)
        .set(['attendee_questions', update_index, 'create'], false)
        .value();

    case Actions.ATTENDEE_QUESTION_DRAG_START:
      // Stash the group/position for the attendee question and mark it as dragging
      var attendee_question = _.find(previousState.attendee_questions, {id: action.payload.id});

      return objectPath(previousState)
        .set(['dragging', 'attendee_questions', 'dragging_id'], attendee_question.id)
        .set(['dragging', 'attendee_questions', 'previous'],
          {position: attendee_question.position})
        .value();

    case Actions.ATTENDEE_QUESTION_DRAG_FINISH:
      // Reset dragging state
      return objectPath(previousState)
        .set(['dragging', 'attendee_questions', 'dragging_id'], null)
        .set(['dragging', 'attendee_questions', 'previous'], null)
      .value();

    case Actions.ATTENDEE_QUESTION_DRAG_CANCEL:
      // Revert state to old group/position
      newState = moveAttendeeQuestion(previousState,
        action.payload.id,
        previousState.dragging.attendee_questions.previous.position);

      return objectPath(newState)
        .set(['dragging', 'attendee_questions', 'dragging_id'], null)
        .set(['dragging', 'attendee_questions', 'previous'], null)
      .value();

    case Actions.ATTENDEE_QUESTION_DRAG_UPDATE:
      return moveAttendeeQuestion(previousState,
        action.payload.item.id,
        action.payload.target_position);

    case Actions.ATTENDEE_QUESTION_CHOICE_ADD:
      update_index =  _.findIndex(previousState.attendee_questions, {id: action.payload.question_id});
      return objectPath.push(previousState, ['attendee_questions', update_index, 'choices'], '');

    case Actions.ATTENDEE_QUESTION_CHOICE_CHANGE:
      var update_question_index =  _.findIndex(previousState.attendee_questions, {id: action.payload.question_id});
      return objectPath.set(previousState, ["attendee_questions", update_question_index, "choices", action.payload.index], action.payload.value);

    case Actions.ATTENDEE_QUESTION_CHOICE_REMOVE:
      update_question_index =  _.findIndex(previousState.attendee_questions, {id: action.payload.question_id});
      return objectPath.del(previousState, ["attendee_questions", update_question_index, "choices", action.payload.index]);

    case Actions.ATTENDEE_QUESTION_CHOICE_DRAG_START:
      // Store details about the item being dragged, and the initial position
      return objectPath(previousState)
        .set(['dragging', 'attendee_question_choice', 'question_id'], action.payload.question_id)
        .set(['dragging', 'attendee_question_choice', 'dragging_id'], action.payload.item.id)
        .set(['dragging', 'attendee_question_choice', 'previous'], action.payload.item.index)
      .value();

    case Actions.ATTENDEE_QUESTION_CHOICE_DRAG_UPDATE:
      // Move the question to the new state
      return moveAttendeeQuestionChoice(previousState, action.payload.question_id, action.payload.item.id, action.payload.index);

    case Actions.ATTENDEE_QUESTION_CHOICE_DRAG_FINISH:
      // Clear drag state tracking data
      return objectPath(previousState)
        .set(['dragging', 'attendee_question_choice', 'question_id'], null)
        .set(['dragging', 'attendee_question_choice', 'dragging_id'], null)
        .set(['dragging', 'attendee_question_choice', 'previous'], null)
      .value();

    case Actions.ATTENDEE_QUESTION_CHOICE_DRAG_CANCEL:
      // Revert question state
      nextState = moveAttendeeQuestionChoice(previousState,
        action.payload.question_id,
        previousState.dragging.attendee_question_choice.dragging_id,
        previousState.dragging.attendee_question_choice.previous
      );

      // Clear drag state tracking data
      return objectPath(nextState)
        .set(['dragging', 'attendee_question_choice', 'question_id'], null)
        .set(['dragging', 'attendee_question_choice', 'dragging_id'], null)
        .set(['dragging', 'attendee_question_choice', 'previous'], null)
      .value();

    default:
      // Initialize reducer state
      newState = objectPath(previousState)
        // Convert various attributes from strings to Dates
        .update(['ticket_classes'], ticket_classes => (() => {
        const result = [];
        for (ticket_class of Array.from(ticket_classes)) {
          for (const attr of ['availability_start', 'availability_finish', 'created_at', 'updated_at', 'deleted_at']) {
            ticket_class = objectPath.set(ticket_class, attr, parseDate(ticket_class[attr]));
          }
          result.push(ticket_class);
        }
        return result;
      })()).update(['promo_codes'], function(promo_codes) {
          const new_promo_codes = (() => {
            const result = [];
            for (let promo_code of Array.from(promo_codes)) {
              for (const attr of ['expires_at']) {
                promo_code = objectPath.set(promo_code, attr, parseDate(promo_code[attr]));
              }
              result.push(promo_code);
            }
            return result;
          })();

          return _.sortBy(new_promo_codes, i => i.code.toLowerCase());
          }).update(['attendee_questions'], attendee_questions => (() => {
        const result = [];
        for (attendee_question of Array.from(attendee_questions)) {
          if (attendee_question.choices.length === 0) {
            result.push(objectPath.set(attendee_question, 'choices', ['', '']));
          } else {
            result.push(attendee_question);
          }
        }
        return result;
      })()).set(['dragging', 'ticket_classes'], {})
        .set(['dragging', 'ticket_groups'], {})
        .set(['dragging', 'attendee_questions'], {})
        .value();

      // Fix the members of each ticket_group based on the id stored on the ticket class
      //
      // In some cases, the server can provide an empty list of ticket IDs for each ticket group
      var tickets_by_group_id = _.groupBy(newState.ticket_classes, i => i.ticket_group_id);

      newState = objectPath(newState)
        .update(['ticket_groups'], ticket_groups => (() => {
        const result = [];
        for (ticket_group of Array.from(ticket_groups)) {
          new_ticket_class_ids = tickets_by_group_id[ticket_group.id] || [];
          new_ticket_class_ids = _.pluck(new_ticket_class_ids, 'id');
          result.push(objectPath.set(ticket_group, 'ticket_class_ids', new_ticket_class_ids));
        }
        return result;
      })()).value();

      newState = updatePayoutAccountSettings(newState);

      // Make sure the initial positions are sane
      return renumberTicketClassPositions(newState);
  }
}
